import { Grid, Modal } from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { buildQueryString } from "../../functions/general";
import {
  abortSilently,
  getIjhsCandidates,
  refreshIjhsPendingCount,
} from "../../functions/getData";
import { showNotification } from "../../functions/snackbar";
import Loader from "../general/Loader";
import NoData from "../general/NoData";
import NunitoText from "../general/NunitoText";
import ReusableTextField from "../general/ReusableTextField";
import EnhancedPagination from "../table/EnhancedPagination";
import IjhsCandidateGroupCard from "./IjhsCandidateGroupCard";
import TagSameStudentModal from "./TagSameStudentModal";

const toTagRows = (members) =>
  members.map((member) => ({
    participant_index: member.participant_index || member.index_no,
    index_no: member.participant_index || member.index_no,
    name: member.name,
    identifier: member.identifier,
    competition_name:
      member.competition?.alias || member.competition?.name || "",
  }));

export default function IjhsAutoDetectionPanel({ setLoading }) {
  const [search, setSearch] = useState("");
  const [queryParams, setQueryParams] = useState({
    limits: 10,
    page: 1,
    search: "",
  });
  const [groupsResponse, setGroupsResponse] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [tagRows, setTagRows] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setLoadingList(true);
    getIjhsCandidates(buildQueryString(queryParams), controller.signal)
      .then((res) => {
        if (!active) return;
        if (!res || Array.isArray(res)) {
          setGroupsResponse(null);
          return;
        }
        setGroupsResponse(res);
      })
      .catch((error) => {
        if (error?.name === "AbortError") return;
        console.log(error);
      })
      .finally(() => {
        if (active) setLoadingList(false);
      });
    return () => {
      active = false;
      abortSilently(controller);
    };
  }, [queryParams, reloadKey]);

  const onChangeSearch = (value) => {
    setSearch(value);
    setQueryParams((prev) => ({ ...prev, search: value, page: 1 }));
  };

  const onChangePage = (pageNo) => {
    setQueryParams((prev) => ({ ...prev, page: pageNo }));
  };

  const afterTagOrDismiss = (data) => {
    setTagRows(null);
    showNotification(
      "success",
      data?.message || "Updated successfully.",
      enqueueSnackbar,
      closeSnackbar
    );
    setReloadKey((key) => key + 1);
    refreshIjhsPendingCount();
  };

  const groups = groupsResponse?.data || [];
  const total = groupsResponse?.meta?.total ?? groups.length;

  return (
    <Grid className="tableContainer" container>
      <NunitoText
        value="IJHS Auto Detection"
        fontSize={40}
        fontWeight={700}
        italic
        color="#144A94"
      />
      <Grid
        className="searchAndBtnContainer"
        container
        alignItems="center"
        justifyContent="space-between"
      >
        <NunitoText
          value={`Potential matches: ${total} groups`}
          fontSize={18}
          fontWeight={600}
          color="#144A94"
        />
        <ReusableTextField
          type="search"
          width={360}
          height={56}
          bgColor="#F2F2F2"
          placeholder="Search name"
          state={search}
          setState={onChangeSearch}
          onBlur
        />
      </Grid>
      {loadingList && <Loader height={400} />}
      {!loadingList && groups.length === 0 && (
        <NoData
          height={400}
          text="No potential matches for your students."
        />
      )}
      {!loadingList &&
        groups.map((group) => (
          <IjhsCandidateGroupCard
            key={group.id}
            group={group}
            setLoading={setLoading}
            onTagSelected={(members) => setTagRows(toTagRows(members))}
            onDismissed={afterTagOrDismiss}
          />
        ))}
      {!loadingList && groupsResponse && (
        <EnhancedPagination
          list={groupsResponse}
          rowsPerPage={queryParams.limits}
          onChangePage={onChangePage}
          title="ijhs-candidates"
        />
      )}
      <Modal open={Boolean(tagRows)} onClose={() => setTagRows(null)}>
        <>
          {tagRows && (
            <TagSameStudentModal
              rows={tagRows}
              setLoading={setLoading}
              onClose={() => setTagRows(null)}
              onSuccess={afterTagOrDismiss}
            />
          )}
        </>
      </Modal>
    </Grid>
  );
}
